import { useCallback, useEffect, useState } from 'react';

import { useSharedSettings } from '../Settings/SharedSettingsContext';

import {
  DateTimeFormat,
  formatDate as utilFormatDate,
  formatDateTime as utilFormatDateTime,
  formatDateTimeWithTimeZone as utilFormatDateTimeWithTimeZone,
  formatDateWithTimeZone as utilFormatDateWithTimeZone,
  formatTimeWithTimeZone as utilFormatTimeWithTimeZone,
  useTimeZoneContext,
} from '@ais-ui/utils';

export const useDateTimeFormat = () => {
  const { settings } = useSharedSettings();
  const { timeZone } = useTimeZoneContext();

  const getUserDateFormat = useCallback((): string => {
    const settingsDateFormat = settings['ribbonPlugin.settings'].date_format_mp;
    return Object.values(DateTimeFormat).includes(settingsDateFormat)
      ? settingsDateFormat
      : DateTimeFormat.STANDARD;
  }, [settings]);

  const [userDateFormat, setUserDateFormat] =
    useState<string>(getUserDateFormat());

  useEffect(() => {
    setUserDateFormat(getUserDateFormat());
  }, [getUserDateFormat, settings]);

  const formatDateTime = useCallback(
    (date: Date | undefined | null, hideSeconds = false): string => {
      return utilFormatDateTime(date, userDateFormat, hideSeconds);
    },
    [userDateFormat],
  );

  const formatDate = useCallback(
    (date: Date | undefined | null): string => {
      return utilFormatDate(date, userDateFormat);
    },
    [userDateFormat],
  );

  const formatDateTimeWithTimeZone = useCallback(
    (date: Date | undefined | null, hideSeconds = false): string => {
      return utilFormatDateTimeWithTimeZone(
        date,
        timeZone,
        userDateFormat,
        hideSeconds,
      );
    },
    [timeZone, userDateFormat],
  );

  const formatDateWithTimeZone = useCallback(
    (date: Date | undefined | null): string => {
      return utilFormatDateWithTimeZone(date, timeZone, userDateFormat);
    },
    [timeZone, userDateFormat],
  );

  const formatTimeWithTimeZone = useCallback(
    (date: Date | undefined | null, hideSeconds = false): string => {
      return utilFormatTimeWithTimeZone(
        date,
        timeZone,
        userDateFormat,
        hideSeconds,
      );
    },
    [timeZone, userDateFormat],
  );

  return {
    formatDateTime,
    formatDate,
    formatDateTimeWithTimeZone,
    formatDateWithTimeZone,
    formatTimeWithTimeZone,
    userDateFormat,
  };
};
